import type {
  DefaultError,
  UseMutationOptions,
} from '@tanstack/react-query'
import type { CartItemsResponse } from '~/lib/services/api/cart'
import { TAG_TYPES } from '@lib/config'

import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { deleteCartItem } from '~/lib/services/api/cart'

// Context type for optimistic updates
interface OptimisticContext {
  previousCartData: CartItemsResponse | undefined
}

function useDeleteCartItem() {
  const queryClient = useQueryClient()

  const mutationFn = async (itemId: string) => {
    const response = await deleteCartItem(itemId)
    if (response.status !== 200)
      throw new Error('Error deleting cart item')
    return response.data
  }

  const options: UseMutationOptions<
    CartItemsResponse,
    DefaultError,
    string,
    OptimisticContext
  > = {
    mutationFn,

    // 🚀 OPTIMISTIC UPDATE: Remove item immediately from cache
    onMutate: async (itemId: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [TAG_TYPES.CART] })

      // Snapshot the previous value
      const previousCartData = queryClient.getQueryData<CartItemsResponse>([
        TAG_TYPES.CART,
      ])

      // Optimistically update to the new value
      if (previousCartData) {
        const optimisticCartData: CartItemsResponse = {
          ...previousCartData,
          items: previousCartData.items.filter(item => item.id !== itemId),
        }

        queryClient.setQueryData([TAG_TYPES.CART], optimisticCartData)

        console.log('🔄 Optimistic delete:', {
          itemId,
          previousCount: previousCartData.items.length,
          newCount: optimisticCartData.items.length,
        })
      }

      // Return a context object with the snapshotted value
      return { previousCartData }
    },

    // ✅ SUCCESS: Keep optimistic update (no need to do anything)
    onSuccess: (data, itemId, _context) => {
      console.log('✅ Delete successful:', { itemId, serverResponse: data })
      // Optionally, you could update with server response if needed:
      // queryClient.setQueryData([TAG_TYPES.CART], data);
    },

    // ❌ ERROR: Rollback to previous state
    onError: (error, itemId, context) => {
      console.error('❌ Delete failed, rolling back:', { itemId, error })

      // Rollback to the previous state
      if (context?.previousCartData) {
        queryClient.setQueryData([TAG_TYPES.CART], context.previousCartData)
      }
    },

    // 🏁 SETTLED: Always run after success or error
    onSettled: (_data, error, itemId, _context) => {
      console.log('🏁 Delete operation settled:', { itemId, success: !error })

      // Invalidate queries to ensure we have the latest data from server
      // This will refetch in the background to sync with server state
      queryClient.invalidateQueries({ queryKey: [TAG_TYPES.CART] })
    },

    // Additional options for better UX
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  }

  return useMutation(options)
}

export default useDeleteCartItem
